import {
  AssignRoleResponse,
  BaseResponse,
  CancelAttendanceResponse,
} from '../generated'

export const formatAssignRoleResponse = (response: AssignRoleResponse) => {
  const { dayOfWeek, month, dayOfMonth, year, timeStart, timeEnd } =
    formatEventDateTime(
      response.localMeetingStartTimestamp,
      response.durationMin
    )

  const title = `Role Confirmation: ${response.selectedRoleName}`
  const description = `
    You have chosen the role of
    ${response.selectedRoleName} for the next event at ${response.addressAsString}, 
    on ${dayOfWeek}, ${month} ${dayOfMonth}, ${year}, ${timeStart} - ${timeEnd}
  `

  return { title, description }
}

export const formatAdminAssignRoleResponse = (
  response: AssignRoleResponse,
  memberName: string
) => {
  const { dayOfWeek, month, dayOfMonth, year, timeStart, timeEnd } =
    formatEventDateTime(
      response.localMeetingStartTimestamp,
      response.durationMin
    )

  const title = `Role Confirmation: ${response.selectedRoleName}`
  const description = `
    ${memberName} has been assigned the role of
    ${response.selectedRoleName} for the next event at ${response.addressAsString},
    on ${dayOfWeek}, ${month} ${dayOfMonth}, ${year}, ${timeStart} - ${timeEnd}
  `

  return { title, description }
}

export const formatAdminRoleCancellationResponse = (
  response: CancelAttendanceResponse,
  memberName: string
) => {
  const { dayOfWeek, month, dayOfMonth, year, timeStart, timeEnd } =
    formatEventDateTime(
      response.localMeetingStartTimestamp,
      response.durationMin
    )

  const title = `Assignment Removed: ${response.selectedRoleName}`
  const description = `
    ${memberName} has been removed as
    ${response.selectedRoleName} for the next event at ${response.addressAsString},
    on ${dayOfWeek}, ${month} ${dayOfMonth}, ${year}, ${timeStart} - ${timeEnd}
  `

  return { title, description }
}

export const formatRoleCancellationResponse = (
  response: CancelAttendanceResponse
) => {
  const { dayOfWeek, month, dayOfMonth, year, timeStart, timeEnd } =
    formatEventDateTime(
      response.localMeetingStartTimestamp,
      response.durationMin
    )

  const title = `Cancellation: ${response.selectedRoleName}`
  const description = `
    Your cancellation for the role of
    ${response.selectedRoleName} for the next event 
    at ${response.addressAsString}, on ${dayOfWeek}, ${month} ${dayOfMonth}, ${year}, ${timeStart} - ${timeEnd} 
    has been successfully processed
  `

  return { title, description }
}

// Utility to format date and time
const formatEventDateTime = (timestamp: number, durationMin: number) => {
  const eventDate = new Date(timestamp * 1000) // Convert seconds to milliseconds
  const dayOfWeek = eventDate.toLocaleDateString('en-US', { weekday: 'long' })
  const month = eventDate.toLocaleDateString('en-US', { month: 'long' })
  const dayOfMonth = eventDate.getDate()
  const year = eventDate.getFullYear()
  const timeStart = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })

  const eventTimeEnd = new Date(eventDate.getTime() + durationMin * 60 * 1000)
  const timeEnd = eventTimeEnd.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })

  return { dayOfWeek, month, dayOfMonth, year, timeStart, timeEnd }
}

export const parseErrorResponse = (response: BaseResponse) => {
  const title =
    response.status.errorCode
      ?.toLowerCase()
      .replace(/_/g, ' ') // Replace underscores with spaces
      .split(' ') // Split the string into words
      .map(capitalizeFirstLetter) // Capitalize each word
      .join(' ') || '' // Join the words back together
  const description = response.status.errorDescription || ''
  return { title, description }
}

export const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1)
